/*
<summary>
   This functional component is all about rendering the Add Defective Item popup and wiring its submit/close behavior.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import { IAddDefectiveItem } from '../../../models/forms/IAddUpdateDefectiveItem';
import { formatMessage } from '../../../translations/format-message';
import AddDefectiveItemForm from '../forms/add-defective-item-form';

interface IProps {
  id: number;
  modalClosed: () => void;
}

/**
 * This function is used to render the Add Defective Item form and handle its submit, success and close flows.
 * @param props: Contains the modalClosed callback invoked when the popup should close
 */
const AddDefectiveItem = (props: IProps) => {
  const { defectiveItemsStore } = useStore();
  const {
    AddDefectiveItemService,
    resetAddDefectiveItemState,
    resetSubjectSuggestions,
    addDefectiveItemState,
    GetDefectiveItemByIdService,
    defectiveItemDetails,
    UpdateDefectiveItemService,
    resetGetDefectiveItemByIdState,
  } = defectiveItemsStore;

  useEffect(() => {
    if (props.id > 0) {
      GetDefectiveItemByIdService(
        Number(getTenantIdFromLocalStorage()),
        props.id
      );
    }
    return () => {
      resetGetDefectiveItemByIdState();
      resetSubjectSuggestions();
    };
  }, [props.id]);

  /**
   * This useEffect is used to show a success toast, reset the store state and close the popup once the item is added.
   */
  useEffect(() => {
    if (addDefectiveItemState.success) {
      if (props.id > 0) {
        toast.success(formatMessage('updated_success'));
      } else {
        toast.success(formatMessage('added_success'));
      }
      resetAddDefectiveItemState();
      resetSubjectSuggestions();
      resetGetDefectiveItemByIdState();
      props.modalClosed();
    }
  }, [addDefectiveItemState.success]);

  /**
   * This useEffect is used to show an error toast and reset the store state whenever the add request fails.
   */
  useEffect(() => {
    if (addDefectiveItemState.error) {
      toast.error(formatMessage(addDefectiveItemState.error));
      resetAddDefectiveItemState();
    }
  }, [addDefectiveItemState.error]);

  /**
   * This function is used to trim the form values and submit the new defective item to the store.
   * @param values: Defective item field values from the form
   */
  const onSubmitForm = ({
    Subject,
    Item,
    Classification1,
    Classification2,
    Comments,
  }: IAddDefectiveItem) => {
    if (props.id > 0) {
      UpdateDefectiveItemService({
        TenantId: Number(getTenantIdFromLocalStorage()),
        Subject: Subject.trim(),
        Item: Item.trim(),
        Classification1: (Classification1 || '').trim(),
        Classification2: (Classification2 || '').trim(),
        Comments: (Comments || '').trim(),
        Id: props.id,
      });
    } else {
      AddDefectiveItemService({
        TenantId: Number(getTenantIdFromLocalStorage()),
        Subject: Subject.trim(),
        Item: Item.trim(),
        Classification1: (Classification1 || '').trim(),
        Classification2: (Classification2 || '').trim(),
        Comments: (Comments || '').trim(),
        Id: 0,
      });
    }
  };

  /**
   * This function is used to reset the subject suggestions and close the popup.
   */
  const onModalClosed = () => {
    resetSubjectSuggestions();
    resetGetDefectiveItemByIdState();
    props.modalClosed();
  };

  return (
    <AddDefectiveItemForm
      id={props.id}
      initialValues={defectiveItemDetails}
      submitHandler={onSubmitForm}
      onModalClosed={onModalClosed}
      isLoading={addDefectiveItemState.inProgress}
    />
  );
};

export default observer(AddDefectiveItem);
